import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Search, UserPlus } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Avatar, Badge, Button, ErrorBox, Input, PageHeader, Select, Spinner } from '@/components/ui';
import { useCan } from '@/lib/auth';
import { plural } from '@/lib/format';
import { departmentsTreeQuery, employeesQuery, flattenTree } from '@/lib/queries';
import { EmployeeForm } from './employee-form';

interface EmployeesSearch {
  q?: string;
  department?: string;
  page?: number;
}

export function EmployeesPage() {
  const search = useSearch({ strict: false }) as EmployeesSearch;
  const navigate = useNavigate();
  const can = useCan();
  const [q, setQ] = useState(search.q ?? '');
  const [creating, setCreating] = useState(false);
  const tree = useQuery(departmentsTreeQuery);
  const page = search.page ?? 1;
  const list = useQuery(
    employeesQuery({ q: search.q, departmentId: search.department, includeSubdepartments: true, page }),
  );

  const setSearch = (patch: Partial<EmployeesSearch>) =>
    navigate({
      to: '/employees',
      search: { q: search.q, department: search.department, page: search.page, ...patch },
      replace: true,
    });

  // Поиск по мере ввода, с паузой — чтобы не дёргать сервер на каждую букву
  const debounce = useRef<number | undefined>(undefined);
  const onQueryChange = (value: string) => {
    setQ(value);
    window.clearTimeout(debounce.current);
    debounce.current = window.setTimeout(
      () => setSearch({ q: value.trim() || undefined, page: undefined }),
      250,
    );
  };
  useEffect(() => () => window.clearTimeout(debounce.current), []);

  const total = list.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / 50));

  return (
    <div>
      <PageHeader
        title="Сотрудники"
        description={list.data ? `${total} ${plural(total, ['человек', 'человека', 'человек'])}` : undefined}
        actions={
          can('org.employee.manage') && (
            <Button onClick={() => setCreating(true)}>
              <UserPlus className="size-4" aria-hidden />
              Добавить сотрудника
            </Button>
          )
        }
      />

      {creating && (
        <div className="panel mb-6 max-w-[760px]">
          <EmployeeForm
            onDone={(id) => {
              setCreating(false);
              if (id) navigate({ to: '/employees/$id', params: { id } });
            }}
          />
        </div>
      )}

      <div className="mb-4 flex flex-wrap gap-3">
        <div className="relative w-full max-w-[360px]">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-steel"
            aria-hidden
          />
          <Input
            type="search"
            value={q}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder="ФИО, телефон или email"
            aria-label="Поиск сотрудников"
            className="pl-9"
          />
        </div>
        <Select
          aria-label="Отдел"
          value={search.department ?? ''}
          onChange={(e) => setSearch({ department: e.target.value || undefined, page: undefined })}
          className="w-auto min-w-[240px]"
        >
          <option value="">Все отделы</option>
          {tree.data &&
            flattenTree(tree.data).map(({ node, depth }) => (
              <option key={node.id} value={node.id}>
                {' '.repeat(depth)}
                {node.name}
              </option>
            ))}
        </Select>
      </div>

      {list.isPending ? (
        <Spinner />
      ) : list.error ? (
        <ErrorBox error={list.error} />
      ) : list.data.items.length === 0 ? (
        <div className="panel px-6 py-10 text-center">
          <p className="font-medium">Никого не нашли</p>
          <p className="mt-1 text-steel">Проверьте написание или выберите другой отдел.</p>
        </div>
      ) : (
        <div className="panel overflow-x-auto">
          <table className="w-full min-w-[720px] text-left">
            <thead>
              <tr className="border-b border-line text-[13px] text-steel">
                <th scope="col" className="px-4 py-2.5 font-medium">
                  Сотрудник
                </th>
                <th scope="col" className="px-4 py-2.5 font-medium">
                  Отдел
                </th>
                <th scope="col" className="px-4 py-2.5 font-medium">
                  Телефон
                </th>
                <th scope="col" className="px-4 py-2.5 font-medium">
                  Email
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {list.data.items.map((e) => (
                <tr key={e.id} className="hover:bg-paper/70">
                  <td className="px-4 py-2">
                    <Link to="/employees/$id" params={{ id: e.id }} className="flex items-center gap-3">
                      <Avatar name={e.fullName} employeeId={e.id} size={30} />
                      <span className="min-w-0">
                        <span className="flex items-center gap-2">
                          <span className="font-medium hover:text-link">{e.fullName}</span>
                          {e.isDepartmentHead && <Badge tone="warn">руководитель</Badge>}
                        </span>
                        <span className="block text-[12.5px] text-steel">
                          {e.positionName ?? 'Должность не указана'}
                        </span>
                      </span>
                    </Link>
                  </td>
                  <td className="px-4 py-2 text-ink-soft">{e.departmentName ?? '—'}</td>
                  <td className="px-4 py-2 tabular-nums">
                    {e.phone ? (
                      <a href={`tel:${e.phone.replace(/[^\d+]/g, '')}`} className="link">
                        {e.phone}
                      </a>
                    ) : (
                      <span className="text-steel">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    {e.email ? (
                      <a href={`mailto:${e.email}`} className="link">
                        {e.email}
                      </a>
                    ) : (
                      <span className="text-steel">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {pages > 1 && (
            <div className="flex items-center justify-between border-t border-line px-4 py-2.5 text-[13px]">
              <span className="text-steel">
                Страница {page} из {pages}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setSearch({ page: page - 1 })}
                >
                  Назад
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={page >= pages}
                  onClick={() => setSearch({ page: page + 1 })}
                >
                  Дальше
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
